/* eslint-disable jsx-a11y/anchor-is-valid */
import React, {FC, useState, useEffect, useRef} from 'react'
import axios from 'axios'
import {DataTable} from 'primereact/datatable'
import {Column} from 'primereact/column'
import {FilterMatchMode} from 'primereact/api'
import {Toast} from 'primereact/toast'
import {InputText} from 'primereact/inputtext'
import {Button} from 'primereact/button'
import {ConfirmPopup, confirmPopup} from 'primereact/confirmpopup'
import {Dialog} from 'primereact/dialog'
import {API_URL} from '../../../../app/modules/auth/core/_requests'
import 'primereact/resources/primereact.min.css'
import 'primeicons/primeicons.css'
import 'primereact/resources/themes/mdc-dark-deeppurple/theme.css'

interface ShopifyStore {
  id: number
  name: string
  shop_url: string
  access_token: string
  blog_id: string
  blog_manuals_id: string
  blog_reviews_id: string
  info: string
}

export const ShopifyStoresPage: FC = () => {
  const toast = useRef<Toast>(null)

  // Table state
  const [shopifyStores, setShopifyStores] = useState<ShopifyStore[]>([])
  const [loading, setLoading] = useState(false)

  // Filters for each column (similar to Brand Tier example)
  const [filters, setFilters] = useState<any>({
    name: {value: null, matchMode: FilterMatchMode.CONTAINS},
    shop_url: {value: null, matchMode: FilterMatchMode.CONTAINS},
    access_token: {value: null, matchMode: FilterMatchMode.CONTAINS},
    info: {value: null, matchMode: FilterMatchMode.CONTAINS},
    blog_id: {value: null, matchMode: FilterMatchMode.CONTAINS},
    blog_manuals_id: {value: null, matchMode: FilterMatchMode.CONTAINS},
    blog_reviews_id: {value: null, matchMode: FilterMatchMode.CONTAINS},
  })

  // Dialog & new row state
  const [showAddDialog, setShowAddDialog] = useState(false)
  const [newRow, setNewRow] = useState<Omit<ShopifyStore, 'id'>>({
    name: '',
    shop_url: '',
    access_token: '',
    info: '',
    blog_id: '',
    blog_manuals_id: '',
    blog_reviews_id: '',
  })

  /**
   * Fetch all rows from DB
   */
  const fetchShopifyStores = async () => {
    setLoading(true)
    try {
      const resp = await axios.get<ShopifyStore[]>(`${API_URL}/api/shopify-stores`)
      setShopifyStores(resp.data || [])
    } catch (err) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to fetch Shopify Stores',
      })
    }
    setLoading(false)
  }

  useEffect(() => {
    fetchShopifyStores()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  /**
   * Inline cell update (PUT)
   */
  const saveCell = async (rowData: ShopifyStore) => {
    setLoading(true)
    try {
      await axios.put(`${API_URL}/api/shopify-stores/${rowData.id}`, rowData)
      toast.current?.show({severity: 'success', summary: 'Updated', detail: 'Cell updated'})
      await fetchShopifyStores()
    } catch (err) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to update cell',
      })
    }
    setLoading(false)
  }

  /**
   * Add a new row (POST)
   */
  const addNewShopifyStore = async () => {
    setLoading(true)
    try {
      await axios.post(`${API_URL}/api/shopify-stores`, newRow)
      toast.current?.show({
        severity: 'success',
        summary: 'Created',
        detail: 'New Shopify Store added',
      })
      setNewRow({
        name: '',
        shop_url: '',
        access_token: '',
        info: '',
        blog_id: '',
        blog_manuals_id: '',
        blog_reviews_id: '',
      })
      setShowAddDialog(false)
      fetchShopifyStores()
    } catch (err) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to create Shopify Store',
      })
    }
    setLoading(false)
  }

  /**
   * Confirm and delete row
   */
  const confirmDelete = (event: React.MouseEvent<HTMLButtonElement>, rowData: ShopifyStore) => {
    confirmPopup({
      target: event.currentTarget,
      message: `Delete store "${rowData.name}"?`,
      icon: 'pi pi-exclamation-triangle',
      acceptClassName: 'p-button-danger',
      accept: () => deleteShopifyStore(rowData),
    })
  }

  const deleteShopifyStore = async (row: ShopifyStore) => {
    setLoading(true)
    try {
      await axios.delete(`${API_URL}/api/shopify-stores/${row.id}`)
      setShopifyStores((prev) => prev.filter((s) => s.id !== row.id))
      toast.current?.show({
        severity: 'info',
        summary: 'Deleted',
        detail: `Removed store "${row.name}"`,
      })
    } catch (err) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to delete Shopify Store',
      })
    }
    setLoading(false)
  }

  /**
   * PrimeReact cell editor for inline text
   */
  const textEditor = (options: any) => {
    const val = options.rowData[options.field]
    const handleBlur = async () => {
      // On blur, trigger the PUT update
      setShopifyStores([...shopifyStores]) // locally re-render
      await saveCell(options.rowData)
    }
    return (
      <InputText
        value={val}
        onChange={(e) => {
          options.rowData[options.field] = e.target.value
          setShopifyStores([...shopifyStores])
        }}
        onBlur={handleBlur}
      />
    )
  }

  /**
   * Dialog footer
   */
  const addDialogFooter = (
    <div className='d-flex justify-content-end'>
      <Button
        label='Cancel'
        className='p-button-text me-2'
        onClick={() => setShowAddDialog(false)}
      />
      <Button label='Save' className='p-button-primary' onClick={addNewShopifyStore} />
    </div>
  )

  return (
    <>
      <Toast ref={toast} />
      <ConfirmPopup />

      <div className='container mt-4'>
        <div className='row mb-3'>
          <div className='col'>
            <h3>Shopify Stores</h3>
          </div>
          <div className='col-auto'>
            <Button
              label='Add Shopify Store'
              icon='pi pi-plus'
              className='p-button-success'
              onClick={() => setShowAddDialog(true)}
            />
          </div>
        </div>

        <DataTable
          value={shopifyStores}
          dataKey='id' // Unique key to identify each row
          editMode='cell'
          paginator
          rows={10}
          rowsPerPageOptions={[10, 20, 50]}
          filters={filters}
          filterDisplay='row'
          loading={loading}
          responsiveLayout='scroll'
          sortMode='multiple'
        >
          {/* Name Column */}
          <Column
            field='name'
            header='Name'
            editor={textEditor}
            filter
            filterPlaceholder='Search name'
            showFilterMenu={false}
            sortable
            style={{width: '200px'}}
          />
          {/* Shop URL */}
          <Column
            field='shop_url'
            header='Shop URL'
            editor={textEditor}
            filter
            filterPlaceholder='Search URL'
            showFilterMenu={false}
            sortable
            style={{width: '200px'}}
          />
          {/* Access Token */}
          <Column
            field='access_token'
            header='Access Token'
            editor={textEditor}
            filter
            filterPlaceholder='Search token'
            showFilterMenu={false}
            sortable
            style={{width: '250px'}}
          />
          {/* Blog Id */}
          <Column
            field='blog_id'
            header='Blog Id'
            editor={textEditor}
            showFilterMenu={false}
            sortable
            style={{width: '200px'}}
          />
          {/* Blog Manuals Id */}
          <Column
            field='blog_manuals_id'
            header='Blog Manuals Id'
            editor={textEditor}
            showFilterMenu={false}
            sortable
            style={{width: '200px'}}
          />
          {/* Blog Reviews Id */}
          <Column
            field='blog_reviews_id'
            header='Blog Reviews Id'
            editor={textEditor}
            showFilterMenu={false}
            sortable
            style={{width: '200px'}}
          />
          {/* Info */}
          <Column
            field='info'
            header='Info'
            editor={textEditor}
            filter
            filterPlaceholder='Search info'
            showFilterMenu={false}
            sortable
            style={{width: '200px'}}
          />
          {/* Delete button column */}
          <Column
            body={(rowData) => (
              <Button
                icon='pi pi-trash'
                className='p-button-text p-button-danger p-button-sm'
                onClick={(e) => confirmDelete(e, rowData)}
              />
            )}
            style={{width: '70px'}}
          />
        </DataTable>
      </div>

      <Dialog
        visible={showAddDialog}
        header='Add New Shopify Store'
        onHide={() => setShowAddDialog(false)}
        style={{width: '400px'}}
        footer={addDialogFooter}
      >
        <div className='row g-3'>
          <div className='col-12'>
            <label className='form-label'>Name</label>
            <InputText
              value={newRow.name}
              onChange={(e) => setNewRow({...newRow, name: e.target.value})}
              className='w-100'
            />
          </div>
          <div className='col-12'>
            <label className='form-label'>Shop URL</label>
            <InputText
              value={newRow.shop_url}
              onChange={(e) => setNewRow({...newRow, shop_url: e.target.value})}
              className='w-100'
            />
          </div>
          <div className='col-12'>
            <label className='form-label'>Access Token</label>
            <InputText
              value={newRow.access_token}
              onChange={(e) => setNewRow({...newRow, access_token: e.target.value})}
              className='w-100'
            />
          </div>
          <div className='col-12'>
            <label className='form-label'>Blog Id</label>
            <InputText
              value={newRow.blog_id}
              onChange={(e) => setNewRow({...newRow, blog_id: e.target.value})}
              className='w-100'
            />
          </div>
          <div className='col-12'>
            <label className='form-label'>Blog Manuals Id</label>
            <InputText
              value={newRow.blog_manuals_id}
              onChange={(e) => setNewRow({...newRow, blog_manuals_id: e.target.value})}
              className='w-100'
            />
          </div>
          <div className='col-12'>
            <label className='form-label'>Blog Reviews Id</label>
            <InputText
              value={newRow.blog_reviews_id}
              onChange={(e) => setNewRow({...newRow, blog_reviews_id: e.target.value})}
              className='w-100'
            />
          </div>
          <div className='col-12'>
            <label className='form-label'>Info</label>
            <InputText
              value={newRow.info}
              onChange={(e) => setNewRow({...newRow, info: e.target.value})}
              className='w-100'
            />
          </div>
        </div>
      </Dialog>
    </>
  )
}

export const ShopifyStoresWrapper: FC = () => {
  return (
    <>
      <h1 className='mt-3 ms-3'>Shopify Stores</h1>
      <ShopifyStoresPage />
    </>
  )
}
